import React from 'react'
import { Sparkle } from 'lucide-react'

export type BadgeVariant = 'ai' | 'success' | 'warning' | 'error' | 'info' | 'required' | 'optional'
export type BadgeSize = 'sm' | 'md'

export interface BadgeProps {
  variant?: BadgeVariant
  size?: BadgeSize
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

const VARIANT_STYLES: Record<BadgeVariant, React.CSSProperties> = {
  ai: {
    background: 'var(--gradient-action)',
    color: 'var(--field-autofill-badge-text)',
    gap: 'var(--spacing-1)',
  },
  success: {
    background: 'var(--color-success-surface)',
    color: 'var(--color-success-text)',
  },
  warning: {
    background: 'var(--color-warning-surface)',
    color: 'var(--color-warning-text)',
  },
  error: {
    background: 'var(--color-error-surface)',
    color: 'var(--color-error-text)',
  },
  info: {
    background: 'var(--color-info-surface)',
    color: 'var(--color-info-text)',
  },
  required: {
    background: 'var(--color-navy-900)',
    color: 'var(--color-text-on-primary)',
  },
  optional: {
    background: 'transparent',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-outline-variant)',
  },
}

const SIZE_STYLES: Record<BadgeSize, React.CSSProperties> = {
  sm: {
    fontSize: 'var(--text-label-xs)',
    padding: '2px 6px',
  },
  md: {
    fontSize: 'var(--text-label-sm)',
    padding: '3px 8px',
  },
}

export function Badge({
  variant = 'info',
  size = 'md',
  children,
  className = '',
  style,
}: BadgeProps) {
  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 'var(--radius-full)',
    fontFamily: 'var(--font-body)',
    fontWeight: 'var(--weight-semibold)',
    lineHeight: 1.2,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    ...VARIANT_STYLES[variant],
    ...SIZE_STYLES[size],
    ...style,
  }

  return (
    <span className={className} style={badgeStyle}>
      {variant === 'ai' && (
        <Sparkle
          size={size === 'sm' ? 12 : 13}
          fill="var(--field-autofill-badge-text)"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  )
}
